import * as React from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Banner } from "@/components/ui/banner";
import { Card } from "@/components/ui/card";
import { ClientNameReveal } from "@/components/clients/ClientNameReveal";
import { NewEvaluationButton } from "@/routes/evaluations/NewEvaluationButton";
import { ReadinessStatusCard } from "@/routes/workspace/ReadinessStatusCard";
import { useListEvaluations, useEvaluationStatusCount } from "@/hooks/queries/useListEvaluations";
import {
  useListScoringResults,
  useResultsThisWeekCount,
} from "@/hooks/queries/useListScoringResults";
import { useListClients } from "@/hooks/queries/useListClients";
import {
  useSubscriptionState,
  isSubscriptionLapsed,
  isSubscriptionLapseConfirmed,
} from "@/hooks/queries/useSubscriptionState";
import { lapsedWriteTooltip } from "@/lib/subscription-copy";
import { isOfflineError } from "@/lib/api-client";
import { OfflineListNotice } from "@/components/pwa/OfflineListNotice";
import { EVALUATION_STATUS_META, humanizeCurrentStep } from "@/lib/evaluation-status";
import { formatShortDate } from "@/lib/format-date";
import type { ClientRecord } from "@/hooks/queries/client-types";
import type { EvaluationListItem } from "@/hooks/queries/evaluation-types";
import type { ScoringResultListItem } from "@/hooks/queries/scoring-types";

// Clinician workspace home (UX Screen 02, Story 6.9). The landing surface after
// sign-in. Live-wired to the endpoints that exist on `develop` (evaluations list,
// clients list, reveal-name, scoring-results list) and, since Story 8.6, the
// Readiness Status Card; the sections whose backing epics are NOT yet built
// (recent-activity timeline) are rendered as NEUTRAL, user-honest empty-states
// with the roadmap pointer in a code comment only — no Epic/Story numbers on
// screen, no fabricated rows (Decisions 1 + 3).
// Tokens only; no pure-white background; tabular-nums on the numeric KPIs.

const CLIENT_LIST_LIMIT = 5;
const RECENT_RESULTS_LIMIT = 5;

/** Time-of-day greeting prefix (cosmetic; the clinician's display name isn't on the token). */
function greetingPrefix(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

/** Compact date + time for the "Last saved" line (e.g. "Apr 20, 2026 · 09:42"). */
function formatSavedAt(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const time = date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `${formatShortDate(iso)} · ${time}`;
}

export function WorkspaceHomeScreen() {
  const subscription = useSubscriptionState();
  // gh-556 — two predicates, two jobs: `lapsed` ("known inactive") keeps gating
  // the write CTAs (the backend 403s every inactive state), while the LAPSE
  // SURFACES (swapped intro + warn banner) fire only on a CONFIRMED lapse —
  // a pre-calibration clinician or a pre-start org contract must not be pointed
  // at a billing problem that does not exist (13.6 AC5).
  const lapsed = isSubscriptionLapsed(subscription);
  const lapseConfirmed = isSubscriptionLapseConfirmed(subscription);
  // gh-568 — source-aware blocked-write reason (org seat → contact the admin).
  const lapsedTooltip = lapsedWriteTooltip(
    subscription,
    "Renew the subscription to continue this evaluation",
  );

  // One query serves BOTH the "Open evaluations" KPI (count) and the in-progress section
  // (the single most-recently-updated draft — the list is ordered updated_at DESC).
  const draftQuery = useListEvaluations({ status: ["draft"], limit: 1 });
  const submittedCount = useEvaluationStatusCount("submitted");
  const clientsQuery = useListClients({ limit: CLIENT_LIST_LIMIT });
  const recentResultsQuery = useListScoringResults({ limit: RECENT_RESULTS_LIMIT });
  const resultsThisWeek = useResultsThisWeekCount();

  const inProgress = draftQuery.data?.results?.[0] ?? null;
  const clientsById = React.useMemo(() => {
    const map = new Map<string, ClientRecord>();
    for (const c of clientsQuery.data?.results ?? []) map.set(c.id, c);
    return map;
  }, [clientsQuery.data]);

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="display-xs text-[color:var(--text-primary)]">{greetingPrefix()}.</h1>
          <p className="text-sm text-[color:var(--text-tertiary)]">
            {lapseConfirmed
              ? "Existing records remain available in read-only mode while new evaluations stay blocked until the subscription is renewed."
              : "Continue in-progress work, start a new evaluation, or review recent AMPS outputs."}
          </p>
        </div>
        <NewEvaluationButton />
      </header>

      {lapseConfirmed ? (
        <Banner variant="warn" title="Your subscription has lapsed.">
          Existing records remain accessible, but new clients and evaluations are disabled until
          billing is renewed.
        </Banner>
      ) : null}

      {/* 4-KPI stats grid — all four live. */}
      <div
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
        role="group"
        aria-label="Workspace summary"
      >
        <KpiTile label="Open evaluations" value={draftQuery.data ? draftQuery.data.count : "—"} />
        <KpiTile
          label="Ready to score"
          value={submittedCount.data ? submittedCount.data.count : "—"}
        />
        {/* Count of results since Monday of the current local week — one tiny
            limit=1 request; em dash while unresolved (loading or offline). */}
        <KpiTile
          label="Results this week"
          value={resultsThisWeek.data ? resultsThisWeek.data.count : "—"}
        />
        <KpiTile label="Active clients" value={clientsQuery.data ? clientsQuery.data.count : "—"} />
      </div>

      <InProgressSection
        inProgress={inProgress}
        client={inProgress ? clientsById.get(inProgress.client_id) : undefined}
        isPending={draftQuery.isPending}
        isError={draftQuery.isError}
        lapsed={lapsed}
        lapsedTooltip={lapsedTooltip}
      />

      <RecentResultsSection query={recentResultsQuery} clientsById={clientsById} />

      <ClientListSection query={clientsQuery} />

      {/* Recent activity — DEFERRED(activity-feed story TBD): the mockup timeline shows
          action-level events (evaluation resumed, results opened, client created) that no
          current endpoint records — synthesizing it from the list endpoints would fabricate
          a different feed. Neutral empty-state until an activity feed exists. */}
      <DeferredSection
        title="Recent activity"
        description="The latest actions across your evaluations and results."
        emptyCopy="No recent activity yet."
      />

      {/* Story 8.6 (gh-76) — Readiness Status Card. Owns its own calibration-status +
          subscription reads and renders nothing until the calibration read resolves, so
          the home still reads as intentional while loading. */}
      <ReadinessStatusCard />
    </section>
  );
}

// A calm, honest "couldn't load" panel for a home-section list whose query failed
// with a NON-offline error (a 4xx/5xx — `isOfflineError` is status-0 only). Without
// this, a server error fell through to the empty-state copy ("No recent results yet."
// / "No clients yet."), silently presenting a failure as genuinely-empty data (9.14
// review). Muted, non-alarm styling per the clinical-UX rationale (desaturated, not
// error-red) — a passive load failure, so role="status", mirroring OfflineListNotice.
function ListErrorNotice({ message }: { message: string }) {
  return (
    <div
      role="status"
      className="flex flex-col items-center gap-2 rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-secondary)] px-6 py-10 text-center"
    >
      <p className="text-md text-[color:var(--text-secondary)]">{message}</p>
      <p className="text-sm text-[color:var(--text-tertiary)]">
        Refresh the page to try again.
      </p>
    </div>
  );
}

function KpiTile({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-5 py-4">
      <div className="text-sm text-[color:var(--text-tertiary)]">{label}</div>
      <div className="tabular-nums text-2xl font-semibold text-[color:var(--text-primary)]">
        {value}
      </div>
    </div>
  );
}

function SectionShell({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-4 rounded-3xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] p-6 shadow-elevated">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">{title}</h2>
          <p className="text-sm text-[color:var(--text-tertiary)]">{description}</p>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function InProgressSection({
  inProgress,
  client,
  isPending,
  isError,
  lapsed,
  lapsedTooltip,
}: {
  inProgress: EvaluationListItem | null;
  client: ClientRecord | undefined;
  isPending: boolean;
  isError: boolean;
  lapsed: boolean;
  /** gh-568 — source-aware blocked-write reason for the disabled Continue CTA. */
  lapsedTooltip: string;
}) {
  return (
    <SectionShell
      title="In-progress evaluation"
      description="Resume the current draft from the exact point where work was paused."
      action={inProgress ? <Badge variant={EVALUATION_STATUS_META.draft.pillVariant}>Draft</Badge> : null}
    >
      {isPending ? (
        <div className="py-6 text-sm text-[color:var(--text-tertiary)]" aria-busy="true">
          Loading your latest draft…
        </div>
      ) : isError ? (
        // A draft-list failure — the same honest "couldn't load" the other sections
        // render, never the false "No draft in progress" (PR #581 review, issue 1).
        // No offline branch needed here: `useListEvaluations` swallows transport
        // failures into the IndexedDB drafts fallback (gh-471 Option A), so an error
        // that escapes it is a real 4xx/5xx, not an offline state.
        <ListErrorNotice message="Couldn't load your in-progress evaluation." />
      ) : !inProgress ? (
        <p className="py-2 text-sm text-[color:var(--text-secondary)]">
          No draft in progress. Start a new evaluation to begin.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Card className="shadow-none">
              <h3 className="text-sm font-semibold text-[color:var(--text-tertiary)]">Client</h3>
              <div className="mt-2 flex flex-col gap-1">
                <ClientNameReveal
                  clientId={inProgress.client_id}
                  initialErased={Boolean(client?.gdpr_erased_at)}
                  erasedAt={client?.gdpr_erased_at ?? null}
                />
                <span
                  className="tabular-nums text-xs text-[color:var(--text-tertiary)]"
                  title={inProgress.client_id}
                >
                  Client ID: {inProgress.client_id}
                </span>
              </div>
            </Card>
            <Card className="shadow-none">
              <h3 className="text-sm font-semibold text-[color:var(--text-tertiary)]">Current step</h3>
              {/* Step-based text, NOT a fabricated progress % (the list contract has no per-item counts). */}
              <p className="mt-2 text-md text-[color:var(--text-primary)]">
                {humanizeCurrentStep(inProgress.current_step, inProgress.state)}
              </p>
            </Card>
            <Card className="shadow-none">
              <h3 className="text-sm font-semibold text-[color:var(--text-tertiary)]">Last saved</h3>
              <p className="mt-2 tabular-nums text-md text-[color:var(--text-primary)]">
                {formatSavedAt(inProgress.updated_at ?? inProgress.created_at)}
              </p>
            </Card>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button asChild variant="secondary">
              <Link to="/evaluations">Open all evaluations</Link>
            </Button>
            {lapsed ? (
              <Button
                variant="primary"
                disabled
                aria-disabled="true"
                title={lapsedTooltip}
              >
                Continue evaluation
              </Button>
            ) : (
              <Button asChild variant="primary">
                <Link to={`/evaluations/${inProgress.id}`}>Continue evaluation</Link>
              </Button>
            )}
          </div>
        </>
      )}
    </SectionShell>
  );
}

// Recent results (Screen 02, Story 9.14 AC7): the caller's most recent scoring
// results — Client (reveal-on-demand + ID helper) / Date / Motor–Process standard
// scores / Open. Navigation keys on evaluation_id (the stepper resolves a `scored`
// evaluation to its results step, Screen 08). Online-only list: a transport failure
// renders the honest offline notice, never the false empty state (gh-471 lesson).
function RecentResultsSection({
  query,
  clientsById,
}: {
  query: ReturnType<typeof useListScoringResults>;
  clientsById: Map<string, ClientRecord>;
}) {
  const results = query.data?.results ?? [];
  const offline = isOfflineError(query.error);
  return (
    <SectionShell
      title="Recent results"
      description="Return to recent scoring outputs and open the full detailed results view."
    >
      {query.isPending ? (
        <div className="py-6 text-sm text-[color:var(--text-tertiary)]" aria-busy="true">
          Loading recent results…
        </div>
      ) : offline ? (
        // Keep any stale cached rows visible UNDER the notice rather than blanking them
        // silently — never let stale results read as current (ClientListSection parity).
        results.length > 0 ? (
          <>
            <OfflineListNotice message="You're offline — showing the last loaded results." />
            <RecentResultsTable results={results} clientsById={clientsById} />
          </>
        ) : (
          <OfflineListNotice />
        )
      ) : query.isError ? (
        // A non-offline failure (4xx/5xx) — an honest "couldn't load", never the
        // false empty-state that a bare `results.length === 0` would render here.
        <ListErrorNotice message="Couldn't load recent results." />
      ) : results.length === 0 ? (
        <p className="py-2 text-sm text-[color:var(--text-secondary)]">
          No recent results yet.
        </p>
      ) : (
        <RecentResultsTable results={results} clientsById={clientsById} />
      )}
    </SectionShell>
  );
}

/** A standard-score pair rendered verbatim ("113.00 / 110.00"); NULL = out of
 * normative range → em dash per side, never 0. */
function formatStdPair(motor: string | null, process: string | null): string {
  return `${motor ?? "—"} / ${process ?? "—"}`;
}

function RecentResultsTable({
  results,
  clientsById,
}: {
  results: ScoringResultListItem[];
  clientsById: Map<string, ClientRecord>;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left text-sm">
        <caption className="sr-only">Your most recent scoring results</caption>
        <thead>
          <tr className="border-b border-[color:var(--bg-quaternary)] text-xs uppercase tracking-wide text-[color:var(--text-tertiary)]">
            <th scope="col" className="px-4 py-3 font-semibold">Client</th>
            <th scope="col" className="px-4 py-3 font-semibold">Date</th>
            <th scope="col" className="px-4 py-3 font-semibold">Motor / Process</th>
            <th scope="col" className="px-4 py-3 font-semibold">Open</th>
          </tr>
        </thead>
        <tbody>
          {results.map((row) => {
            const client = clientsById.get(row.client_id);
            return (
              <tr
                key={row.id}
                className="border-b border-[color:var(--bg-quaternary)] last:border-b-0 align-top"
              >
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1">
                    <ClientNameReveal
                      clientId={row.client_id}
                      initialErased={Boolean(client?.gdpr_erased_at)}
                      erasedAt={client?.gdpr_erased_at ?? null}
                    />
                    <span
                      className="tabular-nums text-xs text-[color:var(--text-tertiary)]"
                      title={row.client_id}
                    >
                      Client ID: {row.client_id}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 align-middle tabular-nums text-[color:var(--text-secondary)]">
                  {formatShortDate(row.created_at)}
                </td>
                <td className="px-4 py-3 align-middle tabular-nums text-[color:var(--text-primary)]">
                  {formatStdPair(row.std_motor, row.std_process)}
                </td>
                <td className="px-4 py-3 align-middle">
                  <Button asChild variant="secondary" size="sm">
                    <Link to={`/evaluations/${row.evaluation_id}`}>Open</Link>
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ClientListSection({ query }: { query: ReturnType<typeof useListClients> }) {
  const clients = query.data?.results ?? [];
  // gh-471 — offline the clients list is online-only (no IDB cache); show a calm offline notice
  // rather than an infinite spinner or a false "No clients yet" empty-state.
  const offline = isOfflineError(query.error);
  return (
    <SectionShell
      title="Client list"
      description="Review core client details before opening a record or starting a new evaluation."
      action={
        <Button asChild variant="secondary" size="sm">
          <Link to="/clients">View all clients</Link>
        </Button>
      }
    >
      {query.isPending ? (
        <div className="py-6 text-sm text-[color:var(--text-tertiary)]" aria-busy="true">
          Loading clients…
        </div>
      ) : offline ? (
        // Offline: show the honest notice. Keep any stale cached rows visible UNDER the notice with a
        // caveat rather than blanking them silently — never let stale clients read as current (gh-471).
        clients.length > 0 ? (
          <>
            <OfflineListNotice message="You're offline — showing the last loaded client list." />
            <ClientList clients={clients} />
          </>
        ) : (
          <OfflineListNotice />
        )
      ) : query.isError ? (
        // A non-offline failure (4xx/5xx) — honest "couldn't load", not the false
        // "No clients yet" empty-state that would otherwise hide a server error.
        <ListErrorNotice message="Couldn't load the client list." />
      ) : clients.length === 0 ? (
        <p className="py-2 text-sm text-[color:var(--text-secondary)]">
          No clients yet — add one to get started.
        </p>
      ) : (
        <ClientList clients={clients} />
      )}
    </SectionShell>
  );
}

function ClientList({ clients }: { clients: ClientRecord[] }) {
  return (
    <ul className="flex flex-col divide-y divide-[color:var(--bg-quaternary)]">
      {clients.map((client) => (
        <li key={client.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
          <ClientNameReveal
            clientId={client.id}
            initialErased={Boolean(client.gdpr_erased_at)}
            erasedAt={client.gdpr_erased_at}
          />
          <span className="tabular-nums text-xs text-[color:var(--text-tertiary)]" title={client.id}>
            {client.id}
          </span>
        </li>
      ))}
    </ul>
  );
}

function DeferredSection({
  title,
  description,
  emptyCopy,
}: {
  title: string;
  description: string;
  emptyCopy: string;
}) {
  return (
    <SectionShell title={title} description={description}>
      <div className="rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-secondary)] px-6 py-8 text-center text-sm text-[color:var(--text-tertiary)]">
        {emptyCopy}
      </div>
    </SectionShell>
  );
}

export default WorkspaceHomeScreen;
